import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Tooltip,
  Link,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  Lightbulb as LightbulbIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  VerifiedUser as VerifiedUserIcon,
  PriorityHigh as PriorityHighIcon,
  Info as InfoIcon,
  AutoAwesome as AutoAwesomeIcon,
  HourglassTop as HourglassTopIcon,
  Stop as StopIcon,
  DeleteOutline as DeleteOutlineIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { collections } from '../../../utils/firestore';
import { REPS_COLORS } from '../utils/constants';
import PatternStrength from '../components/PatternStrength';
import { cancelCaseStudy, deleteCaseStudyWithCascade } from '../../../services/documentService';
import { useAuth } from '../../../contexts/AuthContext';
import { exportCaseStudyPDF, generateCaseStudyPDFBlobUrl } from '../utils/caseStudyPdfExport';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { formatIncidentDate } from '../utils/caseStudyDates.js';
import { parseQuery, snippetsFor } from '../utils/textSearch.js';
import { Highlight, SnippetList } from '../components/Highlight.jsx';
import { getDocumentTexts } from '../../../services/documentService';
import { IconButton } from '@mui/material';

/** REPS quadrant full labels */
const REPS_LABELS = {
  reliability: 'Reliability',
  efficiency: 'Efficiency',
  productivity: 'Productivity',
  safety: 'Safety',
};

/** Source display labels */
const SOURCE_LABELS = {
  csb: 'CSB Report',
  internal: 'Internal',
  audit: 'Audit',
  observation: 'Observation',
  regulatory: 'Regulatory',
  other: 'Other',
};

/** Source chip colors */
const SOURCE_COLORS = {
  csb: '#003366',
  internal: '#00897b',
  audit: '#ef6c00',
  observation: '#7b1fa2',
  regulatory: '#c62828',
  other: '#757575',
};

/** Severity badge config */
const SEVERITY_CONFIG = {
  high: { color: '#c62828', icon: PriorityHighIcon, label: 'High' },
  medium: { color: '#ef6c00', icon: WarningIcon, label: 'Medium' },
  low: { color: '#1565c0', icon: InfoIcon, label: 'Low' },
};

/** OE Element labels */
const OE_ELEMENT_LABELS = {
  driftPerception: 'Drift in Perception',
  driftWork: 'Drift in Work Practices',
  traps: 'Error Traps',
  roadblocks: 'Roadblocks',
  landmines: 'Landmines',
};

/**
 * Format a date value for display.
 */
function formatDate(dateValue) {
  if (!dateValue) return null;
  let date;
  if (dateValue.toDate) {
    date = dateValue.toDate();
  } else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    return null;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** OE element keys + display labels for the progressive analyses section. */
const OE_ELEMENTS_ORDER = [
  { key: 'driftPerception', label: 'Drift in Perception of Risk', color: '#1565c0' },
  { key: 'driftWork', label: 'Drift in How Work is Done', color: '#00897b' },
  { key: 'traps', label: 'Accumulation: Traps', color: '#ef6c00' },
  { key: 'roadblocks', label: 'Accumulation: Roadblocks', color: '#6a1b9a' },
  { key: 'landmines', label: 'Accumulation: Landmines', color: '#c62828' },
];

/** Single OE element analysis block — shows score, confidence, findings, evidence. */
function OEElementBlock({ label, color, data, status }) {
  // Pending: not yet started — skip rendering
  if (status === 'pending' || status === undefined) return null;

  // Analyzing: show placeholder so user sees "thinking" state
  if (status === 'analyzing' || !data) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2.5, mb: 2, borderLeft: 6, borderLeftColor: color, opacity: 0.7 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="h6" fontWeight={700} sx={{ color }}>{label}</Typography>
          <Chip label="Analyzing…" size="small" color="primary" sx={{ ml: 'auto' }} />
        </Box>
      </Paper>
    );
  }

  // Ready: render the full analysis
  const score = data.score;
  const findings = Array.isArray(data.findings) ? data.findings : [];
  const evidence = Array.isArray(data.evidence) ? data.evidence : [];
  const confidence = typeof data.confidence === 'number' ? data.confidence : null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, mb: 2, borderLeft: 6, borderLeftColor: color }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={700} sx={{ color }}>{label}</Typography>
        {typeof score === 'number' && (
          <Chip
            label={`Score ${score}/10`}
            size="small"
            sx={{ bgcolor: color, color: '#fff', fontWeight: 700 }}
          />
        )}
        {confidence !== null && (
          <Chip
            label={`Confidence ${Math.round(confidence * 100)}%`}
            size="small"
            variant="outlined"
          />
        )}
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, ml: 'auto' }} />
      </Box>

      {findings.length > 0 && (
        <Box sx={{ mb: evidence.length > 0 ? 2 : 0 }}>
          <Typography variant="overline" color="text.secondary">Findings</Typography>
          <List dense disablePadding>
            {findings.map((f, i) => (
              <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.5 }} disableGutters>
                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                </ListItemIcon>
                <ListItemText primary={<Typography variant="body2">{f}</Typography>} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {evidence.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">Evidence from the source</Typography>
          <List dense disablePadding>
            {evidence.map((e, i) => (
              <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.25 }} disableGutters>
                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">&ldquo;</Typography>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {e}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
}

/** Sections in the order WF1 initialises them — used for the progress banner. */
const SECTION_ORDER = [
  { key: 'summary', label: 'Summary' },
  { key: 'repsMapping', label: 'REPS Scores' },
  { key: 'driftPerception', label: 'Drift in Perception' },
  { key: 'driftWork', label: 'Drift in Work' },
  { key: 'traps', label: 'Traps' },
  { key: 'roadblocks', label: 'Roadblocks' },
  { key: 'landmines', label: 'Landmines' },
  { key: 'findings', label: 'Findings' },
  { key: 'lessonsLearned', label: 'Lessons' },
  { key: 'precursorPatterns', label: 'Patterns' },
];

/**
 * Demo fallback target: the known-good, fully-analyzed case study to fall back to
 * if the live n8n pipeline stalls. Overridable via env so it's not hard-coupled.
 * Default is the captured Chevron Richmond fixture (see scripts/fixtures/).
 */
const FALLBACK_CASE_STUDY_ID =
  import.meta.env.VITE_DEMO_FALLBACK_CASE_STUDY_ID || 'cs_kfkyQe54DGfV2GXk3BZK';

// If section progress doesn't advance for this long, surface the graceful fallback.
const STALL_WINDOW_MS = 90 * 1000;
// If the n8n pipeline never even creates the case-study skeleton, surface it sooner.
const NO_START_WINDOW_MS = 45 * 1000;

/**
 * Stall detector. Returns true once `active` has been true and `signal` has not
 * changed for `windowMs`. Resets whenever `signal` changes (progress advancing)
 * or `active` goes false. Used to detect a stuck live analysis without flagging
 * normal slow-but-advancing progress.
 */
function useStalled(active, signal, windowMs) {
  const [stalled, setStalled] = useState(false);
  const lastChange = useRef(Date.now());
  const prevSignal = useRef(signal);

  useEffect(() => {
    if (signal !== prevSignal.current) {
      prevSignal.current = signal;
      lastChange.current = Date.now();
      setStalled(false);
    }
  }, [signal]);

  useEffect(() => {
    if (!active) {
      setStalled(false);
      lastChange.current = Date.now();
      return undefined;
    }
    const t = setInterval(() => {
      if (Date.now() - lastChange.current > windowMs) setStalled(true);
    }, 5000);
    return () => clearInterval(t);
  }, [active, windowMs]);

  return stalled;
}

/** Live progress banner — shows which sections the n8n pipeline has finished. */
function AnalysisProgressBanner({ caseStudy, stalled = false, onViewExample, isViewingFallback = false }) {
  const sections = caseStudy?.sections || {};
  const status = caseStudy?.status;

  // No sections map → either old-style case study or analysis not started; hide
  if (!sections || Object.keys(sections).length === 0) return null;

  const total = SECTION_ORDER.length;
  const ready = SECTION_ORDER.filter((s) => sections[s.key] === 'ready').length;
  const allDone = ready === total && status === 'ready';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 3,
        borderLeft: 6,
        borderLeftColor: allDone ? 'success.main' : 'primary.main',
        background: allDone ? 'rgba(46, 125, 50, 0.04)' : 'rgba(21, 101, 192, 0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {allDone ? (
          <>
            <CheckCircleIcon color="success" />
            <Typography variant="subtitle1" fontWeight={700}>
              AI analysis complete — {total} of {total} sections
            </Typography>
          </>
        ) : (
          <>
            <AutoAwesomeIcon color="primary" sx={{ animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            <Typography variant="subtitle1" fontWeight={700}>
              AI analyzing… {ready} of {total} sections complete
            </Typography>
            <Chip label="Live" size="small" color="primary" />
          </>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={(ready / total) * 100}
        color={allDone ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
      />
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {SECTION_ORDER.map((s) => {
          const state = sections[s.key] || 'pending';
          const isReady = state === 'ready';
          const isAnalyzing = state === 'analyzing';
          return (
            <Chip
              key={s.key}
              label={s.label}
              size="small"
              icon={
                isReady ? (
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                ) : isAnalyzing ? (
                  <HourglassTopIcon sx={{ fontSize: 14 }} />
                ) : undefined
              }
              variant={isReady ? 'filled' : 'outlined'}
              color={isReady ? 'success' : isAnalyzing ? 'primary' : 'default'}
              sx={{
                fontSize: '0.7rem',
                opacity: state === 'pending' ? 0.45 : 1,
              }}
            />
          );
        })}
      </Stack>

      {stalled && !allDone && (
        <Alert
          severity="info"
          icon={<HourglassTopIcon fontSize="inherit" />}
          sx={{ mt: 2 }}
          action={
            !isViewingFallback && onViewExample ? (
              <Button color="inherit" size="small" onClick={onViewExample}>
                View a completed example
              </Button>
            ) : undefined
          }
        >
          This analysis is taking longer than usual — the AI pipeline may be busy.
          It will keep updating live here as sections finish.
          {!isViewingFallback && ' Meanwhile, you can view a fully-analyzed example.'}
        </Alert>
      )}
    </Paper>
  );
}

export default function CaseStudyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // P5-08: ?q= carries the library search so the phrase is highlighted here too
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const terms = useMemo(() => parseQuery(searchQuery), [searchQuery]);
  const [docSnippets, setDocSnippets] = useState(null);
  useEffect(() => {
    if (!terms.length || !id) { setDocSnippets(null); return undefined; }
    const docId = typeof id === 'string' && id.startsWith('cs_') ? id.slice(3) : id;
    let cancelled = false;
    getDocumentTexts([docId])
      .then((m) => { if (!cancelled) setDocSnippets(snippetsFor(m[docId] || '', terms, { max: 8, radius: 120 })); })
      .catch(() => { if (!cancelled) setDocSnippets([]); });
    return () => { cancelled = true; };
  }, [id, terms]);
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cancel + Delete state
  const [confirmAction, setConfirmAction] = useState(null); // 'cancel' | 'delete' | null
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  // PDF preview state — { url, filename } when modal open, null when closed
  const [pdfPreview, setPdfPreview] = useState(null);

  const openPdfPreview = () => {
    try {
      const { url, filename } = generateCaseStudyPDFBlobUrl(caseStudy);
      setPdfPreview({ url, filename });
    } catch (err) {
      console.error('PDF preview failed:', err);
      setActionError(err.message || 'Could not generate PDF preview.');
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview(null);
  };

  const downloadFromPreview = () => {
    if (!pdfPreview) return;
    // Anchor + click trick — uses the blob URL we already have, no regeneration
    const a = document.createElement('a');
    a.href = pdfPreview.url;
    a.download = pdfPreview.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Free the blob URL if the user navigates away while the modal is open
  useEffect(() => {
    return () => {
      if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = async () => {
    if (!caseStudy) return;
    setBusy(true);
    setActionError(null);
    try {
      await cancelCaseStudy(caseStudy.id, user?.uid);
      setConfirmAction(null);
      // onSnapshot will pick up the new status; user will see Cancelled state
    } catch (err) {
      setActionError(err.message || 'Could not cancel.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!caseStudy) return;
    setBusy(true);
    setActionError(null);
    try {
      await deleteCaseStudyWithCascade(caseStudy);
      setConfirmAction(null);
      // Navigate away — the snapshot will hit "not found" otherwise
      navigate('/drew/case-studies');
    } catch (err) {
      setActionError(err.message || 'Could not delete.');
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!id) return undefined;
    setLoading(true);
    setError(null);

    // Real-time subscription — case study fills in section-by-section as
    // the n8n pipeline (WF1 → WF2 → WF3) writes back to Firestore.
    const ref = doc(db, collections.caseStudies, id);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setCaseStudy({ id: snap.id, ...snap.data() });
        } else {
          setCaseStudy(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('CaseStudyDetail subscription error:', err);
        setError(err.message || 'Failed to load case study.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [id]);

  const handleBack = () => {
    navigate('/drew/case-studies');
  };

  // --- Live-analysis stall detection (graceful demo fallback) ----------------
  // These hooks must run on every render, before any early return below.
  const sectionsForProgress = caseStudy?.sections || {};
  const readyCount = SECTION_ORDER.filter(
    (s) => sectionsForProgress[s.key] === 'ready'
  ).length;
  const isComplete =
    readyCount === SECTION_ORDER.length && caseStudy?.status === 'ready';
  const csStatus = caseStudy?.status;
  const analysisActive =
    !!caseStudy && !isComplete && csStatus !== 'cancelled' && csStatus !== 'error';
  const progressStalled = useStalled(analysisActive, readyCount, STALL_WINDOW_MS);
  // Skeleton never created — still on the "waiting to start" screen.
  const startStalled = useStalled(!caseStudy && !loading && !error, 0, NO_START_WINDOW_MS);
  const isViewingFallback = id === FALLBACK_CASE_STUDY_ID;
  const handleViewExample = () =>
    navigate(`/drew/case-studies/${FALLBACK_CASE_STUDY_ID}`);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Learning Library
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Not found state — could be a race condition right after upload (WF1 hasn't
  // initialised the cs_{docId} skeleton yet). Subscription stays live, so as
  // soon as WF1 creates the doc, this page auto-rerenders into the full view.
  if (!caseStudy) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Learning Library
        </Button>
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Waiting for analysis to start…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you just uploaded a document, the AI pipeline is initialising. This page will update automatically as soon as the case study record is created (usually within a few seconds).
          </Typography>
          {startStalled && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
                The pipeline is taking longer than usual to start — n8n may be busy.
                This page will still update automatically the moment analysis begins.
              </Alert>
              {!isViewingFallback && (
                <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={handleViewExample}>
                  View a completed example
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  const {
    title,
    summary,
    source,
    industry,
    repsMapping,
    incidentDate,
    createdAt,
    csbUrl,
    keyFindings,
    lessonsLearned,
    tags,
    location,
    company,
    oeElements,
    sections,
  } = caseStudy;

  const isCSB = source === 'csb';
  const sourceLabel = SOURCE_LABELS[source] || source || 'Unknown';
  const sourceColor = SOURCE_COLORS[source] || '#757575';
  // P5-16: the 2024-01-01 extraction placeholder is not a real incident date
  const displayDate = formatIncidentDate(incidentDate, { year: 'numeric', month: 'long', day: 'numeric' }) || formatDate(createdAt);

  const isAnalyzing = caseStudy.status === 'analyzing';
  const isCancelled = caseStudy.status === 'cancelled';

  return (
    <Box>
      {/* Header row: Back button + action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ textTransform: 'none' }}
        >
          Back to Learning Library
        </Button>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!isAnalyzing && (
            <Button
              startIcon={<PictureAsPdfIcon />}
              variant="contained"
              onClick={openPdfPreview}
              sx={{ textTransform: 'none' }}
            >
              View PDF
            </Button>
          )}
          {!isAnalyzing && (
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={() => {
                try {
                  exportCaseStudyPDF(caseStudy);
                } catch (err) {
                  console.error('PDF download failed:', err);
                  setActionError(err.message || 'Could not generate PDF.');
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              Download
            </Button>
          )}
          {isAnalyzing && (
            <Button
              startIcon={<StopIcon />}
              color="warning"
              variant="outlined"
              onClick={() => setConfirmAction('cancel')}
              sx={{ textTransform: 'none' }}
            >
              Cancel Analysis
            </Button>
          )}
          <Button
            startIcon={<DeleteOutlineIcon />}
            color="error"
            variant="outlined"
            onClick={() => setConfirmAction('delete')}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Cancelled banner */}
      {isCancelled && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This analysis was cancelled{caseStudy.cancelledAt ? ` on ${formatDate(caseStudy.cancelledAt)}` : ''}. Partial results may be present below. The original document is still available.
        </Alert>
      )}

      {/* Live progress banner — shows section-by-section AI analysis status */}
      <AnalysisProgressBanner
        caseStudy={caseStudy}
        stalled={progressStalled}
        onViewExample={handleViewExample}
        isViewingFallback={isViewingFallback}
      />

      {/* PDF preview modal — embeds the generated PDF in an iframe */}
      <Dialog
        open={pdfPreview !== null}
        onClose={closePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '92vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <PictureAsPdfIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {pdfPreview?.filename || 'Case Study PDF'}
          </Typography>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            size="small"
            onClick={downloadFromPreview}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
          <IconButton onClick={closePdfPreview} aria-label="Close PDF preview">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#525659' }}>
          {pdfPreview?.url && (
            <iframe
              src={pdfPreview.url}
              title="Case study PDF preview"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Cancel / Delete dialog */}
      <Dialog open={confirmAction !== null} onClose={() => !busy && setConfirmAction(null)}>
        <DialogTitle>
          {confirmAction === 'cancel' ? 'Cancel analysis?' : 'Delete case study?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'cancel' ? (
              <>
                The AI pipeline will be marked as cancelled and this case study will be hidden from the list. The underlying document file remains, and you can re-analyze it later. Any sections already analyzed will be preserved.
              </>
            ) : (
              <>
                This permanently deletes the case study, the uploaded source document, the extracted text cache, and any derived risk alerts, pattern library entries, guidance content, or OE adjustments. This action cannot be undone.
              </>
            )}
          </DialogContentText>
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={busy}>Back</Button>
          <Button
            onClick={confirmAction === 'cancel' ? handleCancel : handleDelete}
            color={confirmAction === 'cancel' ? 'warning' : 'error'}
            variant="contained"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {busy
              ? (confirmAction === 'cancel' ? 'Cancelling…' : 'Deleting…')
              : (confirmAction === 'cancel' ? 'Yes, cancel' : 'Yes, delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header section */}
      <Paper sx={{ p: 3, mb: 3, ...(isCSB && { borderLeft: '4px solid #003366', bgcolor: '#e3f2fd' }) }}>
        {/* Source badge row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {isCSB ? (
            <Tooltip title="U.S. Chemical Safety and Hazard Investigation Board">
              <Chip
                icon={<VerifiedUserIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
                label="CSB Investigation"
                sx={{
                  bgcolor: '#003366',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  height: 28,
                  '& .MuiChip-icon': { ml: '4px' },
                }}
              />
            </Tooltip>
          ) : (
            <Chip
              label={sourceLabel}
              sx={{
                bgcolor: sourceColor,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            />
          )}
          {industry && (
            <Chip
              label={industry.replace(/_/g, ' ')}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          )}
          {displayDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {displayDate}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Title */}
        <Typography variant="h5" fontWeight={700} gutterBottom>
          <Highlight text={title} terms={terms} />
        </Typography>

        {/* Company/Location sub-header */}
        {(company || location) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {[company, location].filter(Boolean).join(' — ')}
          </Typography>
        )}

        {/* CSB Original Report link */}
        {isCSB && csbUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            component={Link}
            href={csbUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 1,
              textTransform: 'none',
              fontWeight: 600,
              color: '#003366',
              borderColor: '#003366',
              '&:hover': {
                borderColor: '#1565c0',
                bgcolor: '#00336608',
              },
            }}
          >
            View Original CSB Report
          </Button>
        )}
      </Paper>

      {/* P5-08: search context — where the phrase appears in the source document */}
      {terms.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Search: “{searchQuery}” — matches are highlighted below
          </Typography>
          {docSnippets === null ? (
            <Typography variant="caption" color="text.secondary">Looking in the source document…</Typography>
          ) : docSnippets.length ? (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                In the source document:
              </Typography>
              <SnippetList snippets={docSnippets} />
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">No further matches in the source document text.</Typography>
          )}
        </Paper>
      )}

      {/* Summary section — shows placeholder while WF3 hasn't run yet */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Summary
        </Typography>
        {summary ? (
          <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
            <Highlight text={summary} terms={terms} />
          </Typography>
        ) : sections?.summary === 'analyzing' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CircularProgress size={14} />
            <Typography variant="body2">Synthesizing summary…</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Summary will appear after the AI finishes synthesizing all five OE element analyses.
          </Typography>
        )}
      </Paper>

      {/* OE Element Analyses — populates live as each branch of WF2 completes */}
      {oeElements && Object.keys(oeElements).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Organizational Entropy Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each of the 5 OE elements is analyzed independently by Claude Opus against the source document. Analyses appear here in real time as the AI completes each one.
          </Typography>
          {OE_ELEMENTS_ORDER.map((el) => (
            <OEElementBlock
              key={el.key}
              label={el.label}
              color={el.color}
              data={oeElements[el.key]}
              status={sections?.[el.key]}
            />
          ))}
        </Box>
      )}

      {/* REPS Mapping Visualization */}
      {repsMapping && Object.keys(repsMapping).length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            REPS Impact Assessment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Scores indicate the level of impact on each REPS quadrant (0 = no impact, 10 = critical impact).
          </Typography>
          <Grid container spacing={3}>
            {Object.entries(REPS_LABELS).map(([key, label]) => {
              const score = repsMapping[key];
              if (score == null) return null;
              const color = REPS_COLORS[key];
              const percentage = (score / 10) * 100;
              return (
                <Grid item xs={12} sm={6} key={key}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {label}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
                        {score}/10
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        bgcolor: `${color}15`,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: color,
                          borderRadius: 6,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Cross-Report Pattern Strength Analysis */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <PatternStrength caseStudyId={id} />
      </Paper>

      <Grid container spacing={3}>
        {/* Key Findings */}
        {keyFindings && keyFindings.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Key Findings
              </Typography>
              <List disablePadding>
                {keyFindings.map((finding, idx) => {
                  const findingText = typeof finding === 'string'
                    ? finding
                    : finding.finding || finding.text || finding.description || '';
                  const severity = typeof finding === 'object' ? finding.severity : null;
                  const oeElement = typeof finding === 'object' ? finding.oeElement : null;
                  const sevConfig = severity ? SEVERITY_CONFIG[severity] : null;
                  const SevIcon = sevConfig ? sevConfig.icon : WarningIcon;

                  return (
                    <React.Fragment key={idx}>
                      {idx > 0 && <Divider component="li" />}
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          <SevIcon sx={{ color: sevConfig ? sevConfig.color : 'text.secondary', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <Highlight text={findingText} terms={terms} />
                              </Typography>
                              {severity && sevConfig && (
                                <Chip
                                  label={sevConfig.label}
                                  size="small"
                                  sx={{
                                    bgcolor: `${sevConfig.color}15`,
                                    color: sevConfig.color,
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: 20,
                                  }}
                                />
                              )}
                              {oeElement && OE_ELEMENT_LABELS[oeElement] && (
                                <Chip
                                  label={OE_ELEMENT_LABELS[oeElement]}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    color: '#7b1fa2',
                                    borderColor: '#7b1fa2',
                                  }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Lessons Learned */}
        {lessonsLearned && lessonsLearned.length > 0 && (
          <Grid item xs={12} md={keyFindings && keyFindings.length > 0 ? 6 : 12}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Lessons Learned
              </Typography>
              <List disablePadding>
                {lessonsLearned.map((lesson, idx) => {
                  const lessonText = typeof lesson === 'string'
                    ? lesson
                    : lesson.lesson || lesson.text || lesson.description || '';
                  const priority = typeof lesson === 'object' ? lesson.priority : null;
                  const applicability = typeof lesson === 'object' ? lesson.applicability : null;

                  return (
                    <React.Fragment key={idx}>
                      {idx > 0 && <Divider component="li" />}
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          <LightbulbIcon sx={{ color: '#f9a825', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                <Highlight text={lessonText} terms={terms} />
                              </Typography>
                              {priority && (
                                <Chip
                                  label={String(priority).toUpperCase()}
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      priority === 'high' || priority === 1
                                        ? '#c6282815'
                                        : priority === 'medium' || priority === 2
                                        ? '#ef6c0015'
                                        : '#1565c015',
                                    color:
                                      priority === 'high' || priority === 1
                                        ? '#c62828'
                                        : priority === 'medium' || priority === 2
                                        ? '#ef6c00'
                                        : '#1565c0',
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    height: 20,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            applicability ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                              >
                                Applies to: {applicability}
                              </Typography>
                            ) : null
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Tags */}
      {tags && tags.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Tags
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tags.map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
